import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getProjectManagementData, updateProjectStatus, getCompensationEntries,
  generateProjectCompensations, updateCompensationStatus, deleteCompensationEntry, deleteCompensationPlan,
  editCompensationEntry, bulkEditCompensations, changeInstallments, createRectification,
  getProjectCompensationConfig, updateProjectCompensationConfig, getActivityTypes, getProjectProducts, getProjectFunctions,
  getProjectContracts, getContractProducts, updateContractProduct, getContracts
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MultiSelect } from '../components/ui/multi-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Search, Loader2, Users, BarChart3, GripVertical, FileText, Hourglass, Coins, RefreshCw, Check, Ban, Wallet, Clock, DollarSign, CheckCircle2, MoreHorizontal, X as XIcon, Pencil, PlusCircle, MinusCircle, RotateCcw, Settings2, Calendar, Tag, Trash2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

const PROJECT_STATUSES = [
  { value: 'in_preparazione', label: 'In Preparazione', color: 'bg-blue-100 text-blue-800', headerColor: 'bg-blue-500 text-white' },
  { value: 'partito', label: 'Partito', color: 'bg-green-100 text-green-800', headerColor: 'bg-green-500 text-white' },
  { value: 'congelato', label: 'Congelato', color: 'bg-yellow-100 text-yellow-800', headerColor: 'bg-red-500 text-white' },
  { value: 'terminato', label: 'Terminato', color: 'bg-gray-100 text-gray-800', headerColor: 'bg-gray-700 text-white' },
];

// Helper function to format days to expiry
function formatDaysToExpiry(days) {
  if (days === null || days === undefined) return null;
  
  if (days < 0) {
    return { text: 'Scaduto', color: 'text-red-600 bg-red-50' };
  } else if (days === 0) {
    return { text: 'Oggi', color: 'text-red-600 bg-red-50' };
  } else if (days <= 7) {
    return { text: `${days}gg`, color: 'text-red-600 bg-red-50' };
  } else if (days <= 30) {
    return { text: `${days}gg`, color: 'text-orange-600 bg-orange-50' };
  } else if (days <= 90) {
    const weeks = Math.floor(days / 7);
    return { text: `${weeks}sett`, color: 'text-orange-600 bg-orange-50' };
  } else if (days <= 365) {
    const months = Math.floor(days / 30);
    return { text: `${months}M`, color: 'text-green-600 bg-green-50' };
  } else {
    const years = Math.floor(days / 365);
    const remainingMonths = Math.floor((days % 365) / 30);
    if (remainingMonths > 0) {
      return { text: `${years}A ${remainingMonths}M`, color: 'text-green-600 bg-green-50' };
    }
    return { text: `${years}A`, color: 'text-green-600 bg-green-50' };
  }
}

// Formatta il valore di un campo personalizzato in base al tipo
function formatCustomFieldValue(def, raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  if (def?.field_type === 'boolean') return raw ? 'Sì' : 'No';
  if (Array.isArray(raw)) return raw.length ? raw.join(', ') : null;
  if (def?.field_type === 'date') {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? String(raw) : d.toLocaleDateString('it-IT');
  }
  return String(raw);
}

// Campi personalizzati progetto valorizzati, nell'ordine di definizione
function getProjectCustomFields(project, defs, { onlyFilled = true } = {}) {
  if (!defs?.length) return [];
  return defs
    .map(def => ({ id: def.id, name: def.name, value: formatCustomFieldValue(def, project?.custom_fields?.[def.id]) }))
    .filter(cf => (onlyFilled ? cf.value !== null : true));
}

// Draggable Project Card Component
function DraggableProjectCard({ project, canEdit, onClick, getInitials, customFieldDefs }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: project.id,
    data: { project },
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  const expiryInfo = formatDaysToExpiry(project.days_to_expiry);
  const customFields = getProjectCustomFields(project, customFieldDefs);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`cursor-pointer hover:shadow-md transition-shadow ${isDragging ? 'shadow-lg ring-2 ring-orange-400' : ''}`}
      data-testid={`project-card-${project.id}`}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0" onClick={onClick}>
            <h4 className="font-medium text-sm truncate" title={project.name}>
              {project.name}
            </h4>
            <p className="text-xs text-gray-500 truncate mt-1">
              {project.client_name}
            </p>
          </div>
          {canEdit && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
              title="Trascina per cambiare stato"
            >
              <GripVertical className="w-4 h-4 text-gray-400" />
            </div>
          )}
        </div>
        
        {/* Contract info row */}
        {project.contracts_count > 0 && expiryInfo && (
          <div className="mt-2" onClick={onClick}>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${expiryInfo.color}`}>
              <FileText className="w-3 h-3" />
              {project.contracts_count}
              <span className="mx-0.5">•</span>
              <Hourglass className="w-3 h-3" />
              {expiryInfo.text}
            </span>
          </div>
        )}
        
        {/* Campi personalizzati progetto */}
        {customFields.length > 0 && (
          <div className="mt-2 flex flex-col gap-1 w-full min-w-0" onClick={onClick} data-testid={`project-card-custom-fields-${project.id}`}>
            {customFields.map(cf => (
              <div
                key={cf.id}
                title={`${cf.name}: ${cf.value}`}
                className="flex items-start gap-1 w-full min-w-0 px-1.5 py-1 rounded-md bg-indigo-50 border border-indigo-100 text-[10px] leading-tight text-indigo-700"
                data-testid={`project-cf-chip-${project.id}-${cf.id}`}
              >
                <Tag className="w-2.5 h-2.5 mt-[1px] shrink-0 text-indigo-400" />
                <span className="min-w-0 break-words">
                  <span className="text-indigo-500">{cf.name}:</span>{' '}
                  <span className="font-medium">{cf.value}</span>
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-3" onClick={onClick}>
          <div className={`flex items-center gap-1 text-xs ${project.people_count === 0 ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
            <Users className="w-3 h-3" />
            <span>{project.people_count}</span>
            {project.people_count === 0 && (
              <span className="ml-1 text-red-500">• Assegnare</span>
            )}
          </div>
          <div className="flex -space-x-2">
            {project.assigned_people?.slice(0, 3).map((person, idx) => (
              <div
                key={idx}
                className="w-6 h-6 rounded-full bg-orange-100 border-2 border-white flex items-center justify-center text-[10px] font-medium text-orange-700"
                title={person.user_name}
              >
                {getInitials(person.user_name)}
              </div>
            ))}
            {project.people_count > 3 && (
              <div className="w-6 h-6 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-[10px] font-medium text-gray-600">
                +{project.people_count - 3}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Droppable Column Component
function DroppableColumn({ status, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: status.value,
  });

  return (
    <div
      ref={setNodeRef}
      className={`bg-gray-50 rounded-lg p-4 transition-colors ${isOver ? 'bg-orange-50 ring-2 ring-orange-300' : ''}`}
    >
      {children}
    </div>
  );
}

// Project Card for Drag Overlay
function ProjectCardOverlay({ project, getInitials, customFieldDefs }) {
  const expiryInfo = formatDaysToExpiry(project.days_to_expiry);
  const customFields = getProjectCustomFields(project, customFieldDefs);
  
  return (
    <Card className="shadow-xl ring-2 ring-orange-400 w-64">
      <CardContent className="p-3">
        <h4 className="font-medium text-sm truncate">{project.name}</h4>
        <p className="text-xs text-gray-500 truncate mt-1">{project.client_name}</p>
        
        {project.contracts_count > 0 && expiryInfo && (
          <div className="mt-2">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${expiryInfo.color}`}>
              <FileText className="w-3 h-3" />
              {project.contracts_count}
              <span className="mx-0.5">•</span>
              <Hourglass className="w-3 h-3" />
              {expiryInfo.text}
            </span>
          </div>
        )}
        
        {customFields.length > 0 && (
          <div className="mt-2 flex flex-col gap-1 w-full min-w-0">
            {customFields.map(cf => (
              <div key={cf.id} className="flex items-start gap-1 w-full min-w-0 px-1.5 py-1 rounded-md bg-indigo-50 border border-indigo-100 text-[10px] leading-tight text-indigo-700">
                <Tag className="w-2.5 h-2.5 mt-[1px] shrink-0 text-indigo-400" />
                <span className="min-w-0 break-words">
                  <span className="text-indigo-500">{cf.name}:</span>{' '}
                  <span className="font-medium">{cf.value}</span>
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-3">
          <div className={`flex items-center gap-1 text-xs ${project.people_count === 0 ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
            <Users className="w-3 h-3" />
            <span>{project.people_count}</span>
            {project.people_count === 0 && (
              <span className="ml-1 text-red-500">• Assegnare</span>
            )}
          </div>
          <div className="flex -space-x-2">
            {project.assigned_people?.slice(0, 3).map((person, idx) => (
              <div
                key={idx}
                className="w-6 h-6 rounded-full bg-orange-100 border-2 border-white flex items-center justify-center text-[10px] font-medium text-orange-700"
              >
                {getInitials(person.user_name)}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ProjectManagementPage() {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [projectFunctions, setProjectFunctions] = useState([]);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const { user, hasRole } = useAuth();
  const { toast } = useToast();

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  // Project detail dialog
  const [selectedProject, setSelectedProject] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState('info');

  // Piano Compensi state
  const [projectComps, setProjectComps] = useState([]);
  const [compsLoading, setCompsLoading] = useState(false);
  const [genMonth, setGenMonth] = useState(new Date().toISOString().slice(0, 7));
  const [generating, setGenerating] = useState(false);

  // Config state
  const [compConfig, setCompConfig] = useState(null);
  const [triggerMode, setTriggerMode] = useState('manual');
  const [configConfirmed, setConfigConfirmed] = useState(false);

  // Per-product triggers & products
  const [projectProducts, setProjectProducts] = useState([]);
  const [productTriggers, setProductTriggers] = useState({});
  const [compPersonFilter, setCompPersonFilter] = useState('all');

  // SF Override state
  const [activityTypes, setActivityTypes] = useState([]);
  const [sfOverrides, setSfOverrides] = useState({});

  // Dialogs
  const [editingEntry, setEditingEntry] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editMonth, setEditMonth] = useState('');
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [bulkEditAmount, setBulkEditAmount] = useState('');
  const [bulkEditTarget, setBulkEditTarget] = useState(null); // {user_id, product_id}
  const [showInstallmentsDialog, setShowInstallmentsDialog] = useState(false);
  const [installmentsTarget, setInstallmentsTarget] = useState(null);
  const [newTotalInstallments, setNewTotalInstallments] = useState('');
  const [showRectifyDialog, setShowRectifyDialog] = useState(false);
  const [rectifyEntry, setRectifyEntry] = useState(null);
  const [rectifyAmount, setRectifyAmount] = useState('');
  const [rectifyDescription, setRectifyDescription] = useState('');

  // Drag state
  const [activeId, setActiveId] = useState(null);

  // Popup "Partito" - modifica date prodotti con scadenza
  const [partitoDialogOpen, setPartitoDialogOpen] = useState(false);
  const [partitoProjectId, setPartitoProjectId] = useState(null);
  const [partitoProducts, setPartitoProducts] = useState([]); // [{cp_id, contract_id, product_name, start_date, end_date}]
  const [partitoSaving, setPartitoSaving] = useState(false);
  const [partitoPendingStatus, setPartitoPendingStatus] = useState(null); // stores context for after dialog

  // Date prodotti nel dialog dettaglio progetto
  const [dialogProducts, setDialogProducts] = useState([]);
  const [dialogProductsLoading, setDialogProductsLoading] = useState(false);
  const [dialogProductsSaving, setDialogProductsSaving] = useState(false);

  const [activeProject, setActiveProject] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Check if user can edit (Admin, PM, or Supervisor of the project)
  const canEditProject = (project) => {
    if (hasRole(['admin'])) return true;
    if (hasRole(['project_manager'])) {
      return project.assigned_people?.some(
        p => p.user_id === user?.id && (p.system_role === 'pm' || p.system_role === 'supervisor')
      );
    }
    return false;
  };

  // Global edit permission (for showing drag handles)
  const canEdit = hasRole(['admin', 'project_manager']);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await getProjectManagementData();
      setProjects(res.data.projects || []);
      setUsers(res.data.users || []);
      setProjectFunctions(res.data.project_functions || []);
      setCustomFieldDefs(res.data.custom_field_definitions || []);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare i dati', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Filter projects
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesName = project.name?.toLowerCase().includes(search);
        const matchesClient = project.client_name?.toLowerCase().includes(search);
        const matchesPerson = project.assigned_people?.some(p => 
          p.user_name?.toLowerCase().includes(search)
        );
        if (!matchesName && !matchesClient && !matchesPerson) return false;
      }

      if (selectedStatuses.length > 0 && !selectedStatuses.includes(project.status)) {
        return false;
      }

      if (selectedFunctions.length > 0) {
        const hasFunction = project.assigned_people?.some(p => 
          selectedFunctions.includes(p.project_function)
        );
        if (!hasFunction) return false;
      }

      if (selectedPeople.length > 0) {
        const hasPerson = project.assigned_people?.some(p => 
          selectedPeople.includes(p.user_id)
        );
        if (!hasPerson) return false;
      }

      return true;
    });
  }, [projects, searchTerm, selectedStatuses, selectedFunctions, selectedPeople]);

  // Group projects by status for Kanban
  const projectsByStatus = useMemo(() => {
    const grouped = {
      in_preparazione: [],
      partito: [],
      congelato: [],
      terminato: [],
    };
    filteredProjects.forEach(project => {
      const status = project.status || 'in_preparazione';
      if (grouped[status]) {
        grouped[status].push(project);
      }
    });
    Object.keys(grouped).forEach(status => {
      grouped[status].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    return grouped;
  }, [filteredProjects]);

  // Workload chart data
  const workloadData = useMemo(() => {
    const userProjects = {};
    
    filteredProjects
      .filter(p => p.status !== 'terminato')
      .forEach(project => {
        project.assigned_people?.forEach(person => {
          // Se ci sono persone selezionate nel filtro, mostra solo quelle
          if (selectedPeople.length > 0 && !selectedPeople.includes(person.user_id)) {
            return;
          }
          
          // Se ci sono funzioni selezionate nel filtro, mostra solo le persone con quella funzione
          if (selectedFunctions.length > 0 && !selectedFunctions.includes(person.project_function)) {
            return;
          }
          
          if (!userProjects[person.user_id]) {
            userProjects[person.user_id] = {
              name: person.user_name,
              projects: [],
            };
          }
          userProjects[person.user_id].projects.push(project.name);
        });
      });

    return Object.values(userProjects)
      .map(u => ({
        name: u.name,
        count: u.projects.length,
        projectNames: u.projects,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);
  }, [filteredProjects, selectedPeople, selectedFunctions]);

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    const project = projects.find(p => p.id === active.id);
    setActiveProject(project);
  };

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    setActiveId(null);
    setActiveProject(null);

    if (!over) return;

    const projectId = active.id;
    const newStatus = over.id;
    const project = projects.find(p => p.id === projectId);

    if (!project || project.status === newStatus) return;

    // Check permission
    if (!canEditProject(project)) {
      toast({ title: 'Errore', description: 'Non hai i permessi per modificare questo progetto', variant: 'destructive' });
      return;
    }

    // Intercept "partito" -> show date edit popup
    if (newStatus === 'partito' && project.status !== 'partito') {
      await openPartitoDialog(projectId, 'drag');
      return;
    }

    // Optimistic update
    setProjects(prev => prev.map(p => 
      p.id === projectId ? { ...p, status: newStatus } : p
    ));

    try {
      await updateProjectStatus(projectId, newStatus);
      const statusLabel = PROJECT_STATUSES.find(s => s.value === newStatus)?.label;
      toast({ title: 'Stato aggiornato', description: `Progetto spostato in "${statusLabel}"` });
    } catch (err) {
      // Revert on error
      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, status: project.status } : p
      ));
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile aggiornare', variant: 'destructive' });
    }
  };

  // Handle status change from dialog
  const handleStatusChange = async (projectId, newStatus) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    // Intercept "partito" -> show date edit popup
    if (newStatus === 'partito' && project.status !== 'partito') {
      // Close project dialog and open partito dialog after animation completes
      setDialogOpen(false);
      setSelectedProject(null);
      setTimeout(() => openPartitoDialog(projectId, 'dialog'), 300);
      return;
    }

    setProjects(prev => prev.map(p => 
      p.id === projectId ? { ...p, status: newStatus } : p
    ));
    setSelectedProject(prev => prev ? { ...prev, status: newStatus } : null);

    try {
      await updateProjectStatus(projectId, newStatus);
      toast({ title: 'Stato aggiornato' });
    } catch (err) {
      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, status: project.status } : p
      ));
      setSelectedProject(prev => prev ? { ...prev, status: project.status } : null);
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile aggiornare', variant: 'destructive' });
    }
  };

  // Open Partito dialog - load contract products with expiration
  const openPartitoDialog = async (projectId, source) => {
    try {
      const pcRes = await getProjectContracts(projectId);
      const activeContracts = (pcRes.data || []).filter(pc => pc.is_active);
      
      const productsWithDates = [];
      for (const pc of activeContracts) {
        const cpRes = await getContractProducts(pc.contract_id);
        const cps = cpRes.data || [];
        for (const cp of cps) {
          // Include SOLO prodotti "open" con has_expiration=true.
          // I prodotti senza scadenza non richiedono date operatività.
          if (cp.status === 'open' && cp.product?.has_expiration === true) {
            productsWithDates.push({
              cp_id: cp.id,
              contract_id: pc.contract_id,
              product_name: cp.product?.name || cp.product_name || 'Prodotto',
              start_date: cp.start_date ? cp.start_date.split('T')[0] : '',
              end_date: cp.end_date ? cp.end_date.split('T')[0] : '',
            });
          }
        }
      }
      
      if (productsWithDates.length === 0) {
        // No products with dates, just proceed with status change
        await confirmPartitoStatusChange(projectId, source);
        return;
      }
      
      setPartitoProjectId(projectId);
      setPartitoProducts(productsWithDates);
      setPartitoPendingStatus(source);
      setPartitoDialogOpen(true);
    } catch (err) {
      console.error('Error loading partito products:', err);
      // Show dialog to ask user if they want to proceed anyway
      toast({ title: 'Attenzione', description: 'Non è stato possibile caricare i prodotti. Procedere comunque?', variant: 'default' });
      await confirmPartitoStatusChange(projectId, source);
    }
  };

  // Confirm status change to "partito" (after dialog or if no products)
  const confirmPartitoStatusChange = async (projectId, source) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, status: 'partito' } : p));
    if (source === 'dialog') {
      setSelectedProject(prev => prev ? { ...prev, status: 'partito' } : null);
    }
    
    try {
      await updateProjectStatus(projectId, 'partito');
      toast({ title: 'Stato aggiornato', description: 'Progetto spostato in "Partito"' });
    } catch (err) {
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, status: project.status } : p));
      if (source === 'dialog') {
        setSelectedProject(prev => prev ? { ...prev, status: project.status } : null);
      }
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile aggiornare', variant: 'destructive' });
    }
  };

  // Save dates from Partito dialog and confirm status
  const handlePartitoSave = async () => {
    setPartitoSaving(true);
    try {
      for (const prod of partitoProducts) {
        await updateContractProduct(prod.contract_id, prod.cp_id, {
          start_date: prod.start_date ? new Date(prod.start_date).toISOString() : null,
          end_date: prod.end_date ? new Date(prod.end_date).toISOString() : null,
        });
      }
      setPartitoDialogOpen(false);
      await confirmPartitoStatusChange(partitoProjectId, partitoPendingStatus);
      toast({ title: 'Date aggiornate', description: 'Date dei prodotti aggiornate con successo' });
    } catch (err) {
      toast({ title: 'Errore', description: 'Errore nel salvataggio date', variant: 'destructive' });
    }
    setPartitoSaving(false);
  };

  // Skip date editing and just change status
  const handlePartitoSkip = async () => {
    setPartitoDialogOpen(false);
    await confirmPartitoStatusChange(partitoProjectId, partitoPendingStatus);
  };

  // Open project detail
  const handleOpenProject = (project) => {
    setSelectedProject(project);
    setDialogTab('info');
    setDialogOpen(true);
    loadDialogProducts(project.id);
  };

  // Load contract products with dates for the dialog
  const loadDialogProducts = async (projectId) => {
    setDialogProductsLoading(true);
    setDialogProducts([]);
    try {
      const pcRes = await getProjectContracts(projectId, true); // include inactive
      const allContracts = pcRes.data || [];
      // Fetch tutti i contratti (una sola call) per lookup del nome
      let contractsMap = {};
      try {
        const contractsRes = await getContracts();
        (contractsRes.data || []).forEach(c => { contractsMap[c.id] = c.name; });
      } catch (_) { /* fallback: usa contract_id */ }
      const prods = [];
      for (const pc of allContracts) {
        const cpRes = await getContractProducts(pc.contract_id);
        const cps = cpRes.data || [];
        for (const cp of cps) {
          // SOLO prodotti con has_expiration=true.
          // I prodotti senza scadenza non hanno date operatività da mostrare/modificare.
          if (cp.product?.has_expiration === true) {
            prods.push({
              cp_id: cp.id,
              contract_id: pc.contract_id,
              contract_name: contractsMap[pc.contract_id] || 'Contratto',
              product_name: cp.product?.name || cp.product_name || 'Prodotto',
              start_date: cp.start_date ? cp.start_date.split('T')[0] : '',
              end_date: cp.end_date ? cp.end_date.split('T')[0] : '',
              status: cp.status || 'open',
            });
          }
        }
      }
      setDialogProducts(prods);
    } catch (err) {
      console.error('Error loading dialog products:', err);
    }
    setDialogProductsLoading(false);
  };

  // Save date changes from dialog
  const saveDialogProductDates = async () => {
    setDialogProductsSaving(true);
    try {
      for (const prod of dialogProducts) {
        await updateContractProduct(prod.contract_id, prod.cp_id, {
          start_date: prod.start_date ? new Date(prod.start_date).toISOString() : null,
          end_date: prod.end_date ? new Date(prod.end_date).toISOString() : null,
        });
      }
      toast({ title: 'Date aggiornate', description: 'Date dei prodotti salvate con successo' });
    } catch (err) {
      toast({ title: 'Errore', description: 'Errore nel salvataggio date', variant: 'destructive' });
    }
    setDialogProductsSaving(false);
  };

  // Load compensations for selected project
  const loadProjectComps = useCallback(async (projectId) => {
    if (!projectId) return;
    setCompsLoading(true);
    try {
      const [compRes, configRes, prodsRes] = await Promise.all([
        getCompensationEntries({ project_id: projectId, include_provisional: true }),
        getProjectCompensationConfig(projectId),
        getProjectProducts(projectId)
      ]);
      setProjectComps(compRes.data || []);
      setProjectProducts(prodsRes.data || []);
      const cfg = configRes.data || {};
      setCompConfig(cfg);
      setTriggerMode(cfg.trigger_mode || 'manual');
      setGenMonth(cfg.trigger_month || new Date().toISOString().slice(0, 7));
      setConfigConfirmed(cfg.confirmed || false);
      setSfOverrides(cfg.sf_overrides || {});
      // Load per-product triggers
      const pt = cfg.product_triggers || {};
      const defaultMonth = cfg.trigger_month || new Date().toISOString().slice(0, 7);
      const prods = prodsRes.data || [];
      const merged = {};
      prods.forEach(p => {
        merged[p.id] = pt[p.id] || { trigger_mode: 'manual', trigger_month: defaultMonth };
      });
      setProductTriggers(merged);
    } catch (err) {
      console.error(err);
    } finally {
      setCompsLoading(false);
    }
  }, []);

  // Load activity types once
  useEffect(() => {
    if (hasRole(['admin'])) {
      getActivityTypes().then(r => setActivityTypes(r.data || [])).catch(() => {});
      getProjectFunctions().then(r => setProjectFunctions(r.data || [])).catch(() => {});
    }
  }, [hasRole]);

  useEffect(() => {
    if (dialogOpen && selectedProject && dialogTab === 'compensi') {
      loadProjectComps(selectedProject.id);
    }
  }, [dialogOpen, selectedProject, dialogTab, loadProjectComps]);

  // Save config + generate
  const handleSaveConfigAndGenerate = async () => {
    if (!selectedProject) return;
    setGenerating(true);
    try {
      const res = await updateProjectCompensationConfig(selectedProject.id, {
        trigger_mode: triggerMode,
        trigger_month: genMonth,
        confirmed: true,
        sf_overrides: sfOverrides,
        product_triggers: productTriggers
      });
      setConfigConfirmed(true);
      
      const info = res.data?.generation_info;
      if (info) {
        if (info.generated > 0) {
          toast({ title: 'Piano generato', description: `${info.generated} voci di compenso create` });
        } else if (info.products_without_config?.length > 0) {
          toast({ 
            title: 'Nessun compenso generato', 
            description: `I seguenti prodotti non hanno configurazioni di compenso: ${info.products_without_config.join(', ')}. Configura i compensi del prodotto in Impostazioni → Prodotti.`,
            variant: 'destructive',
            duration: 10000
          });
        } else if (info.missing_roles?.length > 0) {
          const details = info.missing_roles.map(mr => `${mr.product}: manca ruolo ${mr.roles.join(', ')}`).join('; ');
          toast({ 
            title: 'Nessun compenso generato', 
            description: `Nessun utente assegnato al progetto con il ruolo richiesto. ${details}. Assegna le persone al progetto nella tab "Dettaglio".`,
            variant: 'destructive',
            duration: 10000
          });
        } else if (info.already_exists?.length > 0) {
          toast({ title: 'Piano già esistente', description: `I compensi per ${info.already_exists.join(', ')} esistono già.` });
        } else {
          toast({ title: 'Configurazione salvata', description: 'Nessun nuovo compenso da generare' });
        }
      } else {
        toast({ title: 'Configurazione salvata' });
      }
      
      loadProjectComps(selectedProject.id);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  // Save only SF overrides
  const handleSaveSfOverrides = async () => {
    if (!selectedProject) return;
    try {
      await updateProjectCompensationConfig(selectedProject.id, {
        trigger_mode: triggerMode,
        trigger_month: genMonth,
        confirmed: configConfirmed,
        sf_overrides: sfOverrides,
        product_triggers: productTriggers
      });
      toast({ title: '% Success Fee salvate' });
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const handleGenerateComps = async () => {
    if (!selectedProject) return;
    setGenerating(true);
    try {
      const res = await generateProjectCompensations(selectedProject.id, { trigger_month: genMonth });
      toast({ title: 'Piano generato', description: `${res.data.generated} voci create` });
      loadProjectComps(selectedProject.id);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore generazione', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  // Inline edit single entry
  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    try {
      const updates = { amount: parseFloat(editAmount) };
      if (editMonth && editMonth !== editingEntry.reference_month) {
        updates.reference_month = editMonth;
      }
      await editCompensationEntry(editingEntry.id, updates);
      toast({ title: 'Compenso aggiornato' });
      // Update local state without full reload to preserve scroll position
      setProjectComps(prev => prev.map(c =>
        c.id === editingEntry.id ? { ...c, ...updates } : c
      ));
      setEditingEntry(null);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  // Bulk edit amount
  const handleBulkEdit = async () => {
    if (!bulkEditTarget) return;
    try {
      const res = await bulkEditCompensations({
        project_id: selectedProject.id,
        user_id: bulkEditTarget.user_id,
        product_id: bulkEditTarget.product_id,
        amount: parseFloat(bulkEditAmount)
      });
      toast({ title: 'Rate aggiornate', description: `${res.data.updated} voci modificate` });
      setShowBulkEditDialog(false);
      loadProjectComps(selectedProject.id);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  // Change installments
  const handleChangeInstallments = async () => {
    if (!installmentsTarget) return;
    try {
      const res = await changeInstallments({
        project_id: selectedProject.id,
        user_id: installmentsTarget.user_id,
        product_id: installmentsTarget.product_id,
        new_total: parseInt(newTotalInstallments)
      });
      toast({ title: 'Rate aggiornate', description: res.data.message });
      setShowInstallmentsDialog(false);
      loadProjectComps(selectedProject.id);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  // Rectification
  const handleRectify = async () => {
    if (!rectifyEntry) return;
    try {
      await createRectification({
        original_id: rectifyEntry.id,
        amount: parseFloat(rectifyAmount),
        description: rectifyDescription || 'Rettifica'
      });
      toast({ title: 'Rettifica creata' });
      setShowRectifyDialog(false);
      setRectifyEntry(null);
      loadProjectComps(selectedProject.id);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const handleCompStatusChange = async (entryId, newStatus) => {
    try {
      await updateCompensationStatus(entryId, { status: newStatus });
      toast({ title: 'Aggiornato' });
      loadProjectComps(selectedProject.id);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const handleCompDelete = async (entryId) => {
    try {
      await deleteCompensationEntry(entryId);
      toast({ title: 'Eliminato' });
      loadProjectComps(selectedProject.id);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const handleDeletePlan = async (g) => {
    const locked = g.entries.filter(e => ['approvato', 'saldato'].includes(e.status)).length;
    const msg = `Eliminare il piano compensi di ${g.user_name} - ${g.product_name || 'N/A'}?\n${g.entries.length - locked} rate verranno eliminate anche da Compensi.` + (locked ? `\n${locked} rate approvate/saldate verranno mantenute.` : '');
    if (!window.confirm(msg)) return;
    try {
      const res = await deleteCompensationPlan({ project_ids: [selectedProject.id], user_id: g.user_id, product_id: g.product_id || '' });
      toast({ title: res.data.message });
      loadProjectComps(selectedProject.id);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore', variant: 'destructive' });
    }
  };

  const fmtCurrency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  const COMP_STATUS = {
    in_attesa: { label: 'In Attesa', cls: 'bg-slate-100 text-slate-700', icon: Clock },
    maturato: { label: 'Maturato', cls: 'bg-amber-100 text-amber-700', icon: DollarSign },
    approvato: { label: 'Approvato', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    saldato: { label: 'Saldato', cls: 'bg-blue-100 text-blue-700', icon: Wallet },
    non_approvato: { label: 'Non Approvato', cls: 'bg-red-100 text-red-700', icon: Ban },
  };

  // Group entries by user+product for bulk actions
  const compGroups = useMemo(() => {
    const groups = {};
    projectComps.filter(e => e.origin === 'da_prodotto').forEach(e => {
      const key = `${e.user_id}|${e.product_id || ''}`;
      if (!groups[key]) {
        groups[key] = {
          user_id: e.user_id, user_name: e.user_name, product_id: e.product_id,
          product_name: e.product_name, role: e.role, entries: [], futureCount: 0, total: 0
        };
      }
      groups[key].entries.push(e);
      groups[key].total += e.amount;
      if (e.status === 'in_attesa') groups[key].futureCount++;
    });
    return Object.values(groups);
  }, [projectComps]);

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg max-w-xs">
          <p className="font-semibold">{data.name}</p>
          <p className="text-sm text-gray-600">{data.count} progetti attivi</p>
          <div className="mt-2 text-xs text-gray-500 max-h-32 overflow-y-auto">
            {data.projectNames?.map((name, i) => (
              <p key={i}>• {name}</p>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-management-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gestione Progetti</h1>
        <p className="text-gray-500">
          Vista avanzata con Kanban e analisi carico
          {canEdit && <span className="text-orange-600 ml-2">• Trascina le card per cambiare stato</span>}
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Filtri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Ricerca</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Progetto, cliente, persona..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Stato</Label>
              <MultiSelect
                options={PROJECT_STATUSES.map(s => ({ value: s.value, label: s.label }))}
                selected={selectedStatuses}
                onChange={setSelectedStatuses}
                placeholder="Tutti gli stati"
              />
            </div>

            <div className="space-y-2">
              <Label>Ruolo</Label>
              <MultiSelect
                options={projectFunctions}
                selected={selectedFunctions}
                onChange={setSelectedFunctions}
                placeholder="Tutti i ruoli"
              />
            </div>

            <div className="space-y-2">
              <Label>Persona</Label>
              <MultiSelect
                options={users.map(u => ({ value: u.id, label: u.name }))}
                selected={selectedPeople}
                onChange={setSelectedPeople}
                placeholder="Tutte le persone"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Kanban Board with DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PROJECT_STATUSES.map(status => (
            <DroppableColumn key={status.value} status={status}>
              <div className="flex items-center justify-between mb-4">
                <span className={`px-3 py-1.5 rounded-md font-semibold text-sm ${status.headerColor}`}>
                  {status.label}
                </span>
                <Badge variant="secondary" className="text-xs">
                  {projectsByStatus[status.value]?.length || 0}
                </Badge>
              </div>
              <div className="space-y-3 max-h-[500px] overflow-y-auto min-h-[100px]">
                {projectsByStatus[status.value]?.map(project => (
                  <DraggableProjectCard
                    key={project.id}
                    project={project}
                    canEdit={canEditProject(project)}
                    onClick={() => handleOpenProject(project)}
                    getInitials={getInitials}
                    customFieldDefs={customFieldDefs}
                  />
                ))}
                {projectsByStatus[status.value]?.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    Nessun progetto
                  </p>
                )}
              </div>
            </DroppableColumn>
          ))}
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeProject ? (
            <ProjectCardOverlay project={activeProject} getInitials={getInitials} customFieldDefs={customFieldDefs} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Workload Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Carico per Persona
          </CardTitle>
          <p className="text-sm text-gray-500">
            Numero progetti attivi per persona (esclusi terminati)
          </p>
        </CardHeader>
        <CardContent>
          {workloadData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(300, workloadData.length * 35)}>
              <BarChart data={workloadData} layout="vertical" margin={{ left: 120, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={115} 
                  tick={{ fontSize: 12 }} 
                  tickFormatter={(value) => value.length > 18 ? `${value.substring(0, 16)}...` : value}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#F97316" radius={[0, 4, 4, 0]}>
                  {workloadData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={index < 3 ? '#EF4444' : '#F97316'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-500 py-8">
              Nessun dato disponibile con i filtri selezionati
            </p>
          )}
        </CardContent>
      </Card>

      {/* Project Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{selectedProject?.name}</DialogTitle>
            <DialogDescription>{selectedProject?.client_name}</DialogDescription>
          </DialogHeader>

          {selectedProject && (
            <Tabs value={dialogTab} onValueChange={setDialogTab} className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="info" data-testid="tab-info">
                  <Users className="h-4 w-4 mr-2" /> Dettaglio
                </TabsTrigger>
                <TabsTrigger value="compensi" data-testid="tab-compensi">
                  <Coins className="h-4 w-4 mr-2" /> Piano Compensi
                </TabsTrigger>
              </TabsList>

              {/* Tab Info */}
              <TabsContent value="info" className="space-y-4 overflow-y-auto flex-1 pr-1">
                <div className="space-y-2">
                  <Label>Stato Progetto</Label>
                  {canEditProject(selectedProject) ? (
                    <Select
                      value={selectedProject.status}
                      onValueChange={(value) => handleStatusChange(selectedProject.id, value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_STATUSES.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={PROJECT_STATUSES.find(s => s.value === selectedProject.status)?.color}>
                      {PROJECT_STATUSES.find(s => s.value === selectedProject.status)?.label}
                    </Badge>
                  )}
                </div>

                {selectedProject.description && (
                  <div className="space-y-2">
                    <Label>Descrizione</Label>
                    <p className="text-sm text-gray-600">{selectedProject.description}</p>
                  </div>
                )}

                {/* Campi personalizzati progetto */}
                {customFieldDefs.length > 0 && (
                  <div className="space-y-2" data-testid="project-detail-custom-fields">
                    <Label className="flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5 text-gray-400" />
                      Campi Personalizzati
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {getProjectCustomFields(selectedProject, customFieldDefs, { onlyFilled: false }).map(cf => (
                        <div key={cf.id} className="border rounded-lg px-2.5 py-2 bg-gray-50/50" data-testid={`project-detail-cf-${cf.id}`}>
                          <div className="text-[10px] text-gray-400 uppercase truncate" title={cf.name}>{cf.name}</div>
                          <div className={`text-sm ${cf.value === null ? 'text-gray-300' : 'text-gray-800 font-medium'}`}>
                            {cf.value === null ? 'Non compilato' : cf.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Date Operatività Prodotti */}
                {dialogProducts.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      Date Operatività
                    </Label>
                    <div className="space-y-2">
                      {dialogProducts.map((prod, idx) => (
                        <div key={prod.cp_id} className="border rounded-lg p-2.5 bg-gray-50/50">
                          <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="text-xs font-medium text-gray-700 truncate">{prod.product_name}</span>
                              {prod.contract_name && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-white border border-gray-200 text-gray-600 shrink-0" title={`Contratto: ${prod.contract_name}`}>
                                  <FileText className="w-2.5 h-2.5 text-gray-400" />
                                  <span className="truncate max-w-[180px]">{prod.contract_name}</span>
                                </span>
                              )}
                            </div>
                            {prod.status && prod.status !== 'open' && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                                prod.status === 'renewed' ? 'bg-blue-100 text-blue-700' :
                                prod.status === 'lost' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>{prod.status}</span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-[10px] text-gray-400 uppercase">Inizio</span>
                              {canEditProject(selectedProject) ? (
                                <Input type="date" className="h-8 text-xs" value={prod.start_date}
                                  onChange={e => setDialogProducts(prev => prev.map((p, i) => i === idx ? { ...p, start_date: e.target.value } : p))} />
                              ) : (
                                <div className="text-sm">{prod.start_date || '-'}</div>
                              )}
                            </div>
                            <div>
                              <span className="text-[10px] text-gray-400 uppercase">Fine</span>
                              {canEditProject(selectedProject) ? (
                                <Input type="date" className="h-8 text-xs" value={prod.end_date}
                                  onChange={e => setDialogProducts(prev => prev.map((p, i) => i === idx ? { ...p, end_date: e.target.value } : p))} />
                              ) : (
                                <div className="text-sm">{prod.end_date || '-'}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {canEditProject(selectedProject) && (
                      <div className="flex justify-end pt-2">
                        <Button size="sm" className="gap-1.5 bg-orange-500 hover:bg-orange-600 text-white"
                          onClick={saveDialogProductDates} disabled={dialogProductsSaving}>
                          {dialogProductsSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          Salva Date
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {dialogProductsLoading && (
                  <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Caricamento date...
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Persone Assegnate ({selectedProject.people_count})</Label>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {selectedProject.assigned_people?.map((person, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-xs font-medium text-orange-700">
                            {getInitials(person.user_name)}
                          </div>
                          <span className="text-sm font-medium">{person.user_name}</span>
                        </div>
                        <div className="flex gap-1">
                          {person.system_role && (
                            <Badge variant="outline" className="text-xs">
                              {person.system_role === 'pm' ? 'PM' : 'Sales'}
                            </Badge>
                          )}
                          {person.project_function && (
                            <Badge className="text-xs bg-purple-100 text-purple-800">
                              {projectFunctions.find(f => f.value === person.project_function)?.label || person.project_function}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    {selectedProject.people_count === 0 && (
                      <p className="text-sm text-gray-400 text-center py-2">Nessuna persona assegnata</p>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Tab Piano Compensi */}
              <TabsContent value="compensi" className="overflow-y-auto flex-1 pr-1 space-y-4">
                {/* Per-product Config Section (Admin only) */}
                {hasRole(['admin']) && (
                  <Card className="border border-gray-200">
                    <CardContent className="pt-4 pb-4 px-4 space-y-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Settings2 className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-semibold text-gray-700">Configurazione Piano</span>
                        {configConfirmed && <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px]">Confermato</Badge>}
                      </div>

                      {projectProducts.length === 0 ? (
                        <p className="text-xs text-gray-400">Nessun prodotto associato a questo progetto. Assegna prima un contratto con prodotti.</p>
                      ) : (
                        <div className="space-y-2">
                          <Label className="text-xs text-gray-500">Decorrenza prima rata per prodotto</Label>
                          <div className="grid gap-2">
                            {projectProducts.map(prod => {
                              const pt = productTriggers[prod.id] || { trigger_mode: 'manual', trigger_month: genMonth };
                              return (
                                <div key={prod.id} className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded" data-testid={`product-trigger-${prod.id}`}>
                                  <span className="text-sm font-medium text-gray-700 flex-1 min-w-0 truncate">{prod.name}</span>
                                  <Select value={pt.trigger_mode} onValueChange={v => setProductTriggers(prev => ({
                                    ...prev, [prod.id]: { ...prev[prod.id], trigger_mode: v }
                                  }))}>
                                    <SelectTrigger className="w-36 h-7 text-xs" data-testid={`trigger-mode-${prod.id}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="manual">Mese manuale</SelectItem>
                                      <SelectItem value="auto_partito">Al "Partito"</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {pt.trigger_mode === 'manual' && (
                                    <Input
                                      type="month"
                                      value={pt.trigger_month || genMonth}
                                      onChange={e => setProductTriggers(prev => ({
                                        ...prev, [prod.id]: { ...prev[prod.id], trigger_month: e.target.value }
                                      }))}
                                      className="w-40 h-7 text-xs"
                                      data-testid={`trigger-month-${prod.id}`}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <Button size="sm" onClick={handleSaveConfigAndGenerate} disabled={generating || projectProducts.length === 0} data-testid="confirm-generate-btn">
                        {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                        Conferma e Genera Piano
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Compensations Table */}
                {compsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : projectComps.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Coins className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Nessun compenso generato per questo progetto</p>
                    {hasRole(['admin']) && <p className="text-xs mt-1">Configura e conferma il piano sopra per generare i compensi</p>}
                  </div>
                ) : (
                  <>
                    {/* Summary badges + Person filter */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex gap-2 flex-wrap flex-1">
                        {(() => {
                          const filtered = compPersonFilter === 'all' ? projectComps : projectComps.filter(c => c.user_id === compPersonFilter);
                          const totals = {};
                          filtered.forEach(c => {
                            if (!totals[c.status]) totals[c.status] = { count: 0, amount: 0 };
                            totals[c.status].count++;
                            totals[c.status].amount += c.amount;
                          });
                          return Object.entries(totals).map(([st, v]) => {
                            const cfg = COMP_STATUS[st] || COMP_STATUS.in_attesa;
                            return (
                              <Badge key={st} variant="outline" className={`${cfg.cls} text-xs gap-1 px-2.5 py-1`}>
                                {cfg.label}: {v.count} voci - {fmtCurrency(v.amount)}
                              </Badge>
                            );
                          });
                        })()}
                      </div>
                      <Select value={compPersonFilter} onValueChange={setCompPersonFilter}>
                        <SelectTrigger className="w-48 h-8 text-xs" data-testid="comp-person-filter">
                          <Users className="h-3 w-3 mr-1 text-gray-400" />
                          <SelectValue placeholder="Tutte le persone" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutte le persone</SelectItem>
                          {[...new Map(projectComps.map(c => [c.user_id, { id: c.user_id, name: c.user_name || c.user_id }])).values()].map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Table grouped by product */}
                    {(() => {
                      const filtered = compPersonFilter === 'all' ? projectComps : projectComps.filter(c => c.user_id === compPersonFilter);
                      // Group by product
                      const groups = {};
                      filtered.forEach(entry => {
                        const key = entry.product_id || '_none';
                        if (!groups[key]) groups[key] = { product_id: entry.product_id, product_name: entry.product_name || 'Senza prodotto', entries: [] };
                        groups[key].entries.push(entry);
                      });
                      const productGroups = Object.values(groups);
                      productGroups.forEach(g => g.entries.sort((a, b) => (a.recurrence_index || 0) - (b.recurrence_index || 0) || a.reference_month.localeCompare(b.reference_month)));

                      return productGroups.map(pg => (
                        <div key={pg.product_id || '_none'} className="space-y-1">
                          <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-100 rounded-t-lg border border-b-0 border-gray-200">
                            <Coins className="h-3.5 w-3.5 text-gray-500" />
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{pg.product_name}</span>
                            <Badge variant="outline" className="text-[9px] ml-auto">{pg.entries.length} voci</Badge>
                          </div>
                          <div className="overflow-x-auto max-h-[280px] overflow-y-auto border border-gray-200 rounded-b-lg">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-gray-50/80 sticky top-0">
                                  <TableHead className="text-xs font-semibold uppercase text-gray-500">Mese</TableHead>
                                  <TableHead className="text-xs font-semibold uppercase text-gray-500">Persona</TableHead>
                                  <TableHead className="text-xs font-semibold uppercase text-gray-500">Tipo</TableHead>
                                  <TableHead className="text-xs font-semibold uppercase text-gray-500">Rif.</TableHead>
                                  <TableHead className="text-xs font-semibold uppercase text-gray-500 text-right">Importo</TableHead>
                                  <TableHead className="text-xs font-semibold uppercase text-gray-500">Stato</TableHead>
                                  {hasRole(['admin']) && <TableHead className="w-8"></TableHead>}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {pg.entries.map(entry => {
                                  const stCfg = COMP_STATUS[entry.status] || COMP_STATUS.in_attesa;
                                  const StIcon = stCfg.icon;
                                  const isEditing = editingEntry?.id === entry.id;
                                  const canEdit = ['in_attesa', 'maturato'].includes(entry.status);
                                  const displayMonth = entry.is_provisional ? `Mese ${entry.recurrence_index || '?'}` : entry.reference_month;
                                  return (
                                    <TableRow key={entry.id} className="hover:bg-gray-50/50">
                                      <TableCell className="text-sm font-medium">
                                        {isEditing && !entry.is_provisional ? (
                                          <div className="flex items-center gap-1" data-testid={`edit-month-${entry.id}`}>
                                            <Select value={editMonth.slice(0, 4)} onValueChange={v => setEditMonth(v + editMonth.slice(4))}>
                                              <SelectTrigger className="w-[70px] h-6 text-xs"><SelectValue /></SelectTrigger>
                                              <SelectContent>
                                                {[2024,2025,2026,2027,2028,2029,2030].map(y => (
                                                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                            <Select value={editMonth.slice(5, 7)} onValueChange={v => setEditMonth(editMonth.slice(0, 5) + v)}>
                                              <SelectTrigger className="w-[70px] h-6 text-xs"><SelectValue /></SelectTrigger>
                                              <SelectContent>
                                                {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                                                  <SelectItem key={m} value={m}>{['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'][parseInt(m)-1]}</SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        ) : (
                                          <span className={`${entry.is_provisional ? 'text-amber-600 italic' : ''} ${!entry.is_provisional && canEdit && hasRole(['admin']) ? 'cursor-pointer hover:text-orange-600' : ''}`}
                                            onClick={() => {
                                              if (!entry.is_provisional && canEdit && hasRole(['admin'])) {
                                                setEditingEntry(entry);
                                                setEditAmount(String(entry.amount));
                                                setEditMonth(entry.reference_month);
                                              }
                                            }}
                                            title={entry.is_provisional ? 'Provvisorio - sarà ricalcolato al Partito' : (canEdit && hasRole(['admin']) ? 'Clicca per modificare' : '')}
                                          >
                                            {displayMonth}
                                          </span>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <div className="text-sm">{entry.user_name || '-'}</div>
                                        <div className="text-[10px] text-gray-400 capitalize">{entry.role}</div>
                                      </TableCell>
                                      <TableCell className="text-xs text-gray-500">
                                        {entry.compensation_type_name || '-'}
                                        {entry.is_manual && <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 bg-violet-50 text-violet-600 border-violet-200">Man.</Badge>}
                                      </TableCell>
                                      <TableCell className="text-xs text-gray-500">{entry.reference_info || '-'}</TableCell>
                                      <TableCell className="text-right">
                                        {isEditing ? (
                                          <div className="flex items-center justify-end gap-1">
                                            <Input type="number" step="0.01" className="w-20 h-6 text-xs text-right" value={editAmount}
                                              onChange={e => setEditAmount(e.target.value)} autoFocus
                                              onWheel={e => e.target.blur()}
                                              onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditingEntry(null); }}
                                            />
                                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={handleSaveEdit}><Check className="h-3 w-3 text-emerald-600" /></Button>
                                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditingEntry(null)}><XIcon className="h-3 w-3" /></Button>
                                          </div>
                                        ) : (
                                          <span className={`text-sm font-semibold ${canEdit && hasRole(['admin']) ? 'cursor-pointer hover:text-orange-600' : ''}`}
                                            onClick={() => {
                                              if (canEdit && hasRole(['admin'])) {
                                                setEditingEntry(entry);
                                                setEditAmount(String(entry.amount));
                                                setEditMonth(entry.reference_month);
                                              }
                                            }}
                                            title={canEdit && hasRole(['admin']) ? 'Clicca per modificare' : ''}
                                          >
                                            {fmtCurrency(entry.amount)}
                                          </span>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant="outline" className={`${stCfg.cls} text-[10px] gap-0.5`}>
                                          <StIcon className="h-3 w-3" /> {stCfg.label}
                                        </Badge>
                                      </TableCell>
                                      {hasRole(['admin']) && (
                                        <TableCell>
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                                <MoreHorizontal className="h-3.5 w-3.5" />
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-48">
                                              {entry.status === 'maturato' && (
                                                <>
                                                  <DropdownMenuItem onClick={() => handleCompStatusChange(entry.id, 'approvato')}>
                                                    <Check className="h-3.5 w-3.5 mr-2 text-emerald-600" /> Approva
                                                  </DropdownMenuItem>
                                                  <DropdownMenuItem onClick={() => handleCompStatusChange(entry.id, 'non_approvato')}>
                                                    <Ban className="h-3.5 w-3.5 mr-2 text-red-500" /> Rifiuta
                                                  </DropdownMenuItem>
                                                </>
                                              )}
                                              {entry.status === 'approvato' && (
                                                <DropdownMenuItem onClick={() => handleCompStatusChange(entry.id, 'saldato')}>
                                                  <Wallet className="h-3.5 w-3.5 mr-2 text-blue-600" /> Segna Saldato
                                                </DropdownMenuItem>
                                              )}
                                              {['maturato', 'approvato', 'saldato'].includes(entry.status) && (
                                                <DropdownMenuItem onClick={() => {
                                                  setRectifyEntry(entry);
                                                  setRectifyAmount('');
                                                  setRectifyDescription('');
                                                  setShowRectifyDialog(true);
                                                }}>
                                                  <RotateCcw className="h-3.5 w-3.5 mr-2 text-orange-500" /> Rettifica
                                                </DropdownMenuItem>
                                              )}
                                              {canEdit && (
                                                <DropdownMenuItem onClick={() => handleCompDelete(entry.id)} className="text-red-600">
                                                  <XIcon className="h-3.5 w-3.5 mr-2" /> Elimina
                                                </DropdownMenuItem>
                                              )}
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      ));
                    })()}
                  </>
                )}

                {/* Bulk Actions per Group */}
                {hasRole(['admin']) && compGroups.length > 0 && (
                  <Card className="border border-gray-200">
                    <CardContent className="pt-4 pb-4 px-4 space-y-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Azioni Piano Fisso</span>
                      {compGroups.map((g, idx) => (
                        <div key={idx} className="flex items-center gap-2 flex-wrap px-3 py-2 bg-gray-50 rounded">
                          <span className="text-sm font-medium text-gray-700 flex-1 min-w-0 truncate">
                            {g.user_name} - {g.product_name || 'N/A'} <span className="text-gray-400">({g.entries.length} rate, {g.futureCount} future)</span>
                          </span>
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => {
                            setBulkEditTarget({ user_id: g.user_id, product_id: g.product_id });
                            setBulkEditAmount(String(g.entries[0]?.amount || 0));
                            setShowBulkEditDialog(true);
                          }} data-testid={`bulk-edit-btn-${idx}`}>
                            <Pencil className="h-3 w-3" /> Modifica tutte
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => {
                            setInstallmentsTarget({ user_id: g.user_id, product_id: g.product_id });
                            setNewTotalInstallments(String(g.entries.length));
                            setShowInstallmentsDialog(true);
                          }} data-testid={`change-installments-btn-${idx}`}>
                            <PlusCircle className="h-3 w-3" /> Rate
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeletePlan(g)} data-testid={`delete-plan-btn-${idx}`}>
                            <Trash2 className="h-3 w-3" /> Elimina piano
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* SF Override Section (Admin only) - below table */}
                {hasRole(['admin']) && activityTypes.length > 0 && (
                  <Card className="border border-gray-200">
                    <CardContent className="pt-4 pb-4 px-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-semibold text-gray-700">Override % Success Fee</span>
                        </div>
                        <Button size="sm" variant="outline" onClick={handleSaveSfOverrides} data-testid="save-sf-overrides-btn">
                          Salva %
                        </Button>
                      </div>
                      <p className="text-[11px] text-gray-400">Sovrascrivi le percentuali globali delle Tipologie Attività per questo progetto, per funzione</p>
                      <div className="grid gap-3">
                        {activityTypes.map(at => {
                          const ov = sfOverrides[at.id] || {};
                          const globalFees = at.function_fee_percentages || {};
                          return (
                            <div key={at.id} className="border border-gray-200 rounded p-3 space-y-2">
                              <span className="text-sm font-semibold text-gray-700">{at.name}</span>
                              <div className="grid grid-cols-2 gap-2">
                                {projectFunctions.map(fn => {
                                  const globalVal = globalFees[fn.value] || 0;
                                  return (
                                    <div key={fn.value} className="flex items-center gap-1">
                                      <Label className="text-[10px] text-gray-500 w-24 truncate" title={fn.label}>{fn.label}</Label>
                                      <Input
                                        type="number" step="0.1" min="0" max="100"
                                        className="w-16 h-6 text-xs text-center"
                                        placeholder={String(globalVal)}
                                        value={ov[fn.value] ?? ''}
                                        onWheel={e => e.target.blur()}
                                        onChange={e => {
                                          const val = e.target.value;
                                          setSfOverrides(prev => ({
                                            ...prev,
                                            [at.id]: { ...prev[at.id], [fn.value]: val === '' ? undefined : parseFloat(val) }
                                          }));
                                        }}
                                        data-testid={`sf-override-${fn.value}-${at.id}`}
                                      />
                                      <span className="text-[10px] text-gray-400">%</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Amount Dialog */}
      <Dialog open={showBulkEditDialog} onOpenChange={setShowBulkEditDialog}>
        <DialogContent className="max-w-sm" data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifica Tutte le Rate Future</DialogTitle>
            <DialogDescription>Nuovo importo per tutte le rate "In Attesa"</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nuovo Importo</Label>
              <Input type="number" step="0.01" value={bulkEditAmount} onChange={e => setBulkEditAmount(e.target.value)} data-testid="bulk-edit-amount" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkEditDialog(false)}>Annulla</Button>
            <Button onClick={handleBulkEdit} data-testid="bulk-edit-confirm">Modifica Tutte</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Installments Dialog */}
      <Dialog open={showInstallmentsDialog} onOpenChange={setShowInstallmentsDialog}>
        <DialogContent className="max-w-sm" data-testid="installments-dialog">
          <DialogHeader>
            <DialogTitle>Modifica Numero Rate</DialogTitle>
            <DialogDescription>Aggiungi o rimuovi rate per questa combinazione</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nuovo Totale Rate</Label>
              <Input type="number" min="1" value={newTotalInstallments} onChange={e => setNewTotalInstallments(e.target.value)} data-testid="new-total-installments" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInstallmentsDialog(false)}>Annulla</Button>
            <Button onClick={handleChangeInstallments} data-testid="installments-confirm">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rectification Dialog */}
      <Dialog open={showRectifyDialog} onOpenChange={setShowRectifyDialog}>
        <DialogContent className="max-w-sm" data-testid="rectify-dialog">
          <DialogHeader>
            <DialogTitle>Rettifica Compenso</DialogTitle>
            <DialogDescription>
              Crea una voce correttiva per: {rectifyEntry?.user_name} - {rectifyEntry?.reference_month} ({fmtCurrency(rectifyEntry?.amount)})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Importo Rettifica (es. -50 per storno, +100 per aggiunta)</Label>
              <Input type="number" step="0.01" value={rectifyAmount} onChange={e => setRectifyAmount(e.target.value)} placeholder="Es. -50 o +100" data-testid="rectify-amount" />
            </div>
            <div>
              <Label className="text-xs">Descrizione</Label>
              <Textarea value={rectifyDescription} onChange={e => setRectifyDescription(e.target.value)} placeholder="Motivazione rettifica..." data-testid="rectify-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRectifyDialog(false)}>Annulla</Button>
            <Button onClick={handleRectify} data-testid="rectify-confirm">Crea Rettifica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog "Partito" - Modifica Date Prodotti */}
      <Dialog open={partitoDialogOpen} onOpenChange={setPartitoDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Progetto in partenza
            </DialogTitle>
            <DialogDescription>
              Conferma o modifica le date di inizio e fine operatività dei prodotti con scadenza associati a questo progetto.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[400px] overflow-y-auto">
            {partitoProducts.map((prod, idx) => (
              <div key={prod.cp_id} className="border rounded-lg p-3 space-y-2 bg-gray-50/50">
                <div className="font-medium text-sm text-gray-800">{prod.product_name}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Data Inizio</Label>
                    <Input type="date" value={prod.start_date}
                      onChange={e => setPartitoProducts(prev => prev.map((p, i) => i === idx ? { ...p, start_date: e.target.value } : p))}
                      data-testid={`partito-start-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Data Fine</Label>
                    <Input type="date" value={prod.end_date}
                      onChange={e => setPartitoProducts(prev => prev.map((p, i) => i === idx ? { ...p, end_date: e.target.value } : p))}
                      data-testid={`partito-end-${idx}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={handlePartitoSkip} className="text-gray-500">
              Salta
            </Button>
            <Button onClick={handlePartitoSave} disabled={partitoSaving} className="bg-green-600 hover:bg-green-700">
              {partitoSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-1" />}
              Conferma e Parti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
